import Service, { inject as service } from '@ember/service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

/**
 * Results Calculator Service
 *
 * Calculates race results from timing data and reference data.
 * Mirrors the Entry.getResultRecord() logic from the backend exactly.
 *
 * All lookups use UUID-based keys:
 * - Entries looked up by entry.uuid
 * - Timing records looked up by entry.uuid (entryUuid field)
 * - Classes looked up via entry.classUuid
 * - ClassGroups looked up via class.classGroupUuid
 */
export default class ResultsCalculatorService extends Service {
  @service eventData;

  /**
   * Calculate results for all entries in a classgroup
   * Returns array of result objects matching Entry.getResultRecord() structure
   */
  calculateResultsForClassGroup(eventId, classGroupId) {
    // Access _dataVersion to establish tracking dependency for reactivity
    // eslint-disable-next-line no-unused-vars
    const version = this.eventData._dataVersion;

    const eventDataObj = this.eventData.getEventData(eventId);
    if (!eventDataObj) {
      console.warn(`[ResultsCalculator] Event ${eventId} data not found`);
      return [];
    }

    // Check if we have the minimum required data (entries and classes)
    if (eventDataObj.entries.size === 0) {
      console.warn(`[ResultsCalculator] Event ${eventId} has no entries loaded yet`);
      return [];
    }

    const entries = this.eventData.getEntriesForClassGroup(eventId, classGroupId);
    const course = this.eventData.getCourseForClassGroup(eventId, classGroupId);
    const event = eventDataObj.event;

    // Use default precision if event metadata not available
    const precision = event?.precision || 1000;

    console.log(`[ResultsCalculator] Calculating results for classgroup ${classGroupId}:`, {
      entriesCount: entries.length,
      startRecordsCount: eventDataObj.startRecords.size,
      finishRecordsCount: eventDataObj.finishRecords.size,
      hasEvent: !!event,
      precision
    });

    if (entries.length === 0) {
      console.warn(`[ResultsCalculator] No entries found for classgroup ${classGroupId}`);
      return [];
    }

    // Build expected timing points from course
    const expectedTimingPoints = this.buildExpectedTimingPoints(course);

    // Calculate result for each entry
    const results = entries.map(entry => {
      return this.calculateEntryResult(eventDataObj, entry, expectedTimingPoints, precision);
    });

    // Sort results matching BullittTime Live sorting logic:
    // 1. Results with finish time and OK status come first, sorted by time
    // 2. Results in progress (with splits but no finish) come next, sorted by furthest split
    // 3. Results with no timing data come last, sorted by bib
    results.sort((a, b) => {
      const aTime = a.totalTimeInMilliseconds;
      const bTime = b.totalTimeInMilliseconds;

      // If both have totalTimes and are OK, sort by them
      if (aTime != null && bTime != null && a.status === 'OK' && b.status === 'OK') {
        if (aTime !== bTime) {
          return aTime > bTime ? 1 : -1;
        }
        // Use tie order if present
        if (a.tieOrder !== null && b.tieOrder !== null) {
          return a.tieOrder - b.tieOrder;
        }
        return a.uuid > b.uuid ? 1 : -1;
      }

      // If a has a totalTime and is OK, it is on top
      if (aTime != null && a.status === 'OK') {
        return -1;
      }
      // If b has a totalTime and is OK, it is on top
      if (bTime != null && b.status === 'OK') {
        return 1;
      }

      // Neither has a finish time - look at splits to determine who is furthest along
      const aSplits = a.splits || [];
      const bSplits = b.splits || [];

      let aNumSplits = 0;
      let bNumSplits = 0;
      let aLastSplitIndex = -1;
      let bLastSplitIndex = -1;

      aSplits.forEach((split, index) => {
        if (split.elapsed != null) {
          aNumSplits++;
          if (index > aLastSplitIndex) {
            aLastSplitIndex = index;
          }
        }
      });

      bSplits.forEach((split, index) => {
        if (split.elapsed != null) {
          bNumSplits++;
          if (index > bLastSplitIndex) {
            bLastSplitIndex = index;
          }
        }
      });

      // If neither has splits, sort by bib
      if (aNumSplits === 0 && bNumSplits === 0) {
        if (a.bib != null && b.bib != null) {
          return a.bib > b.bib ? 1 : -1;
        }
        return a.uuid > b.uuid ? 1 : -1;
      }

      // More splits = further along = higher in results
      if (aNumSplits !== bNumSplits) {
        return aNumSplits > bNumSplits ? -1 : 1;
      }

      // Same number of splits - sort by elapsed time at last split
      const aLastElapsed = aSplits[aLastSplitIndex]?.elapsed;
      const bLastElapsed = bSplits[bLastSplitIndex]?.elapsed;

      if (aLastElapsed != null && bLastElapsed != null) {
        return aLastElapsed > bLastElapsed ? 1 : aLastElapsed < bLastElapsed ? -1 : 0;
      }

      return 0;
    });

    // Assign places - ONLY to entries with status === 'OK' AND totalTimeInMilliseconds !== null
    let currentPlace = 1;
    let lastTime = null;
    let tiePlace = null;

    for (const result of results) {
      // Initialize place to empty string for all results
      result.place = "";

      // Only assign place if status is OK and has a time
      if (result.status === 'OK' && result.totalTimeInMilliseconds !== null) {
        // Handle ties
        if (result.totalTimeInMilliseconds === lastTime) {
          // This is a tie
          if (tiePlace === null) {
            tiePlace = currentPlace - 1;
          }
          result.place = tiePlace;
        } else {
          tiePlace = null;
          result.place = currentPlace;
        }

        currentPlace++;
        lastTime = result.totalTimeInMilliseconds;
      }
    }

    return results;
  }

  /**
   * Calculate result for a single entry
   * Matches Entry.getResultRecord() structure exactly
   * Uses UUID-based lookups for timing records and class relationships
   */
  calculateEntryResult(eventDataObj, entry, expectedTimingPoints, precision = 1000) {
    const event = eventDataObj.event;
    // Use passed precision or fallback to event precision or default
    const eventPrecision = precision || event?.precision || 1000;

    // Initialize result with all entry fields
    const result = {
      // Entry basic info (uuid-based)
      uuid: entry.uuid,
      status: entry.status,
      bib: entry.bib,
      firstName: entry.firstName,
      lastName: entry.lastName,
      name: [entry.firstName, entry.lastName].filter(Boolean).join(' '),
      id2: entry.id2 && entry.id2 !== 0 ? entry.id2 : "",
      id3: entry.id3 && entry.id3 !== 0 ? entry.id3 : "",
      id1: entry.id1,
      club: entry.club,
      town: entry.town,
      state: entry.state,
      country: entry.country,
      gender: entry.gender,
      DOB: entry.DOB,
      text1: entry.text1,
      text2: entry.text2,
      text3: entry.text3,
      pts1: entry.pts1,
      pts2: entry.pts2,
      pts3: entry.pts3,
      pts4: entry.pts4,
      pts5: entry.pts5,
      racePts1: entry.racePts1,
      racePts2: entry.racePts2,
      racePts3: entry.racePts3,
      racePts4: entry.racePts4,
      racePts5: entry.racePts5,
      eventUuid: entry.eventUuid,
      seedGroup: entry.seedGroup,

      // Timing defaults
      assignedStartTime: undefined,
      startTime: undefined,
      finishTime: undefined,
      tieOrder: null,

      // Class info (uuid-based)
      className: "",
      classUuid: null,
      classGroupName: "",
      classGroupUuid: null,
      courseUuid: null,

      // Splits and times
      splits: undefined,
      finishSplit: null,
      penaltyTimeInMilliseconds: null,
      totalTimeWithoutPenaltyInMilliseconds: null,
      penaltyTimeFormatted: "",
      totalTimeWithoutPenaltyFormatted: "",
      totalTimeInMilliseconds: null,
      totalTimeFormatted: ""
    };

    // Get timing records using entry.uuid as key
    const startRecord = eventDataObj.startRecords.get(entry.uuid);
    const finishRecord = eventDataObj.finishRecords.get(entry.uuid);
    const intermediateRecords = eventDataObj.intermediateRecords.get(entry.uuid) || [];
    const tieRecord = eventDataObj.tieTimingRecords.get(entry.uuid);
    const penaltyRecords = eventDataObj.penaltyTimingRecords.get(entry.uuid) || [];

    // Debug: log lookup for specific bib numbers
    if (entry.bib === 187 || entry.bib === '187') {
      console.log(`[ResultsCalculator] Bib 187 lookup:`, {
        entryUuid: entry.uuid,
        hasFinishRecord: !!finishRecord,
        finishRecordsKeys: Array.from(eventDataObj.finishRecords.keys()).slice(0, 10),
        finishRecord: finishRecord
      });
    }

    // Get class info using UUID-based lookups
    const entryClass = eventDataObj.classes.get(entry.classUuid);
    if (entryClass) {
      result.className = entryClass.name;
      result.classUuid = entryClass.uuid;

      const classGroup = eventDataObj.classGroups.get(entryClass.classGroupUuid);
      if (classGroup) {
        result.classGroupName = classGroup.name;
        result.classGroupUuid = classGroup.uuid;
        result.courseUuid = classGroup.courseUuid;
      }
    }

    // Start time - use flattened format (startTime field directly)
    if (startRecord) {
      result.assignedStartTime = startRecord.assignedStartTime;
      // New format uses flattened startTime directly
      result.startTime = startRecord.startTime || result.assignedStartTime;
    }

    // Finish time - use flattened format (finishTime field directly)
    if (finishRecord) {
      // New format uses flattened finishTime directly
      result.finishTime = finishRecord.finishTime;
    }

    // Tie order
    if (tieRecord) {
      result.tieOrder = tieRecord.order;
    }

    // Calculate splits
    const splits = [];
    let lastSplitTime = result.startTime;

    if (intermediateRecords.length > 0 && expectedTimingPoints && expectedTimingPoints.length > 0) {
      // Filter to only iTRs with time (flattened format uses 'time' directly)
      const iTRs = intermediateRecords.filter(iTR => iTR.time != null);

      // Sort by time
      iTRs.sort((a, b) => {
        const aTime = a.time;
        const bTime = b.time;
        return aTime > bTime ? 1 : -1;
      });

      // Match against expected timing points (using UUIDs)
      let currentIndex = 0;

      for (const eTP of expectedTimingPoints) {
        let foundTP = false;

        for (let i = currentIndex; i < iTRs.length; i++) {
          const iTR = iTRs[i];

          // Match by intermediateTimingPointUuid
          if (iTR.intermediateTimingPointUuid === eTP.intermediateTimingPointUuid) {
            let splitTime = null;

            if (eTP.isPublic) {
              splitTime = dayjs(iTR.time).diff(dayjs(lastSplitTime));
              lastSplitTime = iTR.time;
            }

            const elapsed = dayjs(iTR.time).diff(dayjs(result.startTime));
            splits.push({
              courseElementUuid: eTP.courseElementUuid,
              courseElementIndex: eTP.courseElementIndex,
              name: eTP.name,
              isPublic: eTP.isPublic,
              intermediateTimingPointUuid: eTP.intermediateTimingPointUuid,
              intermediateTimingPointName: eTP.intermediateTimingPointName,
              time: iTR.time,
              split: splitTime,
              splitFormatted: splitTime !== null ? this.formatTime(splitTime, eventPrecision) : null,
              elapsed: elapsed,
              elapsedFormatted: this.formatTime(elapsed, eventPrecision)
            });

            currentIndex = i + 1;
            foundTP = true;
            break;
          }
        }

        if (!foundTP) {
          splits.push({
            courseElementUuid: eTP.courseElementUuid,
            courseElementIndex: eTP.courseElementIndex,
            name: eTP.name,
            isPublic: eTP.isPublic,
            intermediateTimingPointUuid: eTP.intermediateTimingPointUuid,
            intermediateTimingPointName: eTP.intermediateTimingPointName,
            time: null,
            split: null,
            splitFormatted: null,
            elapsed: null,
            elapsedFormatted: null
          });
        }
      }
    }

    result.splits = splits;

    // Create splitsMap for easy lookup by course element UUID in templates
    // (keyed by courseElementUuid, not intermediateTimingPointUuid, because
    // multiple course elements can reference the same timing point in lap races)
    result.splitsMap = {};
    for (const split of splits) {
      result.splitsMap[split.courseElementUuid] = split;
    }

    // Calculate total time
    let totalTimeInMilliseconds = null;
    let penaltyInMilliseconds = 0;

    if (result.finishTime != undefined && result.startTime != undefined) {
      result.finishSplit = dayjs(result.finishTime).diff(dayjs(lastSplitTime));
      result.finishSplitFormatted = this.formatTime(result.finishSplit, eventPrecision);
      totalTimeInMilliseconds = dayjs(result.finishTime).diff(dayjs(result.startTime));

      // Floor to event precision (in 1000s of a second)
      totalTimeInMilliseconds = Math.floor(totalTimeInMilliseconds / eventPrecision) * eventPrecision;
    }

    // Calculate penalties
    if (penaltyRecords.length > 0) {
      for (const penalty of penaltyRecords) {
        // Use UTC to avoid timezone issues - penalty.time stores duration as time of day
        const pTime = dayjs.utc(penalty.time);
        const isBonus = penalty.isBonus;
        let penaltyTime = pTime.hour() * 1000 * 60 * 60 +
                          pTime.minute() * 1000 * 60 +
                          pTime.second() * 1000 +
                          pTime.millisecond();

        if (isBonus) {
          penaltyTime = -penaltyTime;
        }

        penaltyInMilliseconds += penaltyTime;
      }
    }

    result.penaltyTimeInMilliseconds = penaltyInMilliseconds;
    result.totalTimeWithoutPenaltyInMilliseconds = totalTimeInMilliseconds;

    // Format penalty time
    if (penaltyInMilliseconds !== null && penaltyInMilliseconds !== 0) {
      result.penaltyTimeFormatted = this.formatTime(penaltyInMilliseconds, eventPrecision);
    }

    // Format total time with penalty
    if (totalTimeInMilliseconds !== null) {
      totalTimeInMilliseconds += penaltyInMilliseconds;
      result.totalTimeInMilliseconds = totalTimeInMilliseconds;

      result.totalTimeFormatted = this.formatTime(totalTimeInMilliseconds, eventPrecision);
      result.totalTimeWithoutPenaltyFormatted = this.formatTime(result.totalTimeWithoutPenaltyInMilliseconds, eventPrecision);
    }

    return result;
  }

  /**
   * Build expected timing points from course
   * Uses UUID-based fields from transformed course data
   * Returns course elements with their UUIDs for unique identification
   * (multiple course elements can reference the same intermediate timing point)
   */
  buildExpectedTimingPoints(course) {
    // New format uses courseElements (camelCase), fallback to CourseElements for compatibility
    const courseElements = course?.courseElements || course?.CourseElements;
    if (!course || !courseElements) {
      return [];
    }

    return courseElements
      .filter(ce => ce.intermediateTimingPointUuid !== null)
      .sort((a, b) => a.order - b.order)
      .map((ce, index) => ({
        courseElementUuid: ce.uuid,
        courseElementOrder: ce.order,
        courseElementIndex: index,
        name: ce.name,
        isPublic: ce.isPublic,
        intermediateTimingPointUuid: ce.intermediateTimingPointUuid,
        intermediateTimingPointName: ce.intermediateTimingPointName || ce.name
      }));
  }

  /**
   * Format time based on event precision
   * Matches backend formatting exactly
   */
  formatTime(milliseconds, precision) {
    if (milliseconds === null || milliseconds === undefined) {
      return "";
    }

    // Handle negative times (penalties/bonuses)
    const isNegative = milliseconds < 0;
    const absMilliseconds = Math.abs(milliseconds);

    // Get base HH:mm:ss format
    const base = dayjs.utc(absMilliseconds).format('HH:mm:ss');
    const ms = absMilliseconds % 1000;

    let formatted;

    switch (precision) {
      case 1000: // seconds
        formatted = base;
        break;
      case 100: // tenths
        formatted = `${base}.${Math.floor(ms / 100)}`;
        break;
      case 10: // hundredths
        formatted = `${base}.${String(Math.floor(ms / 10)).padStart(2, '0')}`;
        break;
      case 1: // milliseconds
        formatted = `${base}.${String(ms).padStart(3, '0')}`;
        break;
      default:
        formatted = base;
    }

    return isNegative ? `-${formatted}` : formatted;
  }
}
